import type { ReactNode } from "react";

import { FOOTER_COLORS } from "./footerColors";

export type SocialLinkId = "youtube" | "note" | "x";

type SocialLink = {
  id: SocialLinkId;
  label: string;
  href: string;
  glyph: ReactNode;
};

/** ゆめさくの公式アカウント。足すときはここだけ直す */
export const YUMESAKU_SOCIAL_LINKS: Record<SocialLinkId, SocialLink> = {
  youtube: {
    id: "youtube",
    label: "YouTube",
    href: "https://www.youtube.com/@yumesaku_engineer",
    glyph: (
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <path d="M9.5 7.5v9l7.5-4.5z" fill="currentColor" />
      </svg>
    ),
  },
  note: {
    id: "note",
    label: "note",
    href: "https://note.com/luagate",
    glyph: (
      <span aria-hidden="true" style={{ fontWeight: 900, fontSize: 14, lineHeight: 1 }}>
        n
      </span>
    ),
  },
  x: {
    id: "x",
    label: "X",
    href: "https://x.com/yumesaku_tech",
    glyph: (
      <span aria-hidden="true" style={{ fontWeight: 900, fontSize: 14, lineHeight: 1 }}>
        X
      </span>
    ),
  },
};

/** 公式アカウントを並べる順。フッターとエラーの頁の「公式SNS」はどちらもここから読む (2 か所に書かない) */
export const YUMESAKU_SOCIAL_ORDER: readonly SocialLinkId[] = ["youtube", "x", "note"];

export interface SocialLinksProps {
  /** 並べる順。省くと YUMESAKU_SOCIAL_ORDER の全部 */
  ids?: readonly SocialLinkId[];
  /** 地が暗い面では札の字を白に寄せる */
  tone?: "light" | "dark";
  align?: "start" | "center";
  className?: string;
}

/**
 * 公式アカウントへのリンク。色の丸と名前を並べる。
 * 白一色の絵だけだと何のサービスか分からない、という声があったので名前も出す
 */
export function SocialLinks({
  ids = YUMESAKU_SOCIAL_ORDER,
  tone = "light",
  align = "start",
  className,
}: SocialLinksProps) {
  /* 各サービスの色の丸は目立ちすぎる (代表 10/23「主張が強すぎ」) ので、地になじむ 1 色の丸と薄い字にする */
  const labelColor = tone === "dark" ? "rgba(255,255,255,0.72)" : FOOTER_COLORS.light.head;
  const circleBackground = tone === "dark" ? "rgba(255,255,255,0.10)" : FOOTER_COLORS.light.border;
  const glyphColor = tone === "dark" ? "rgba(255,255,255,0.78)" : FOOTER_COLORS.light.head;
  return (
    <ul
      className={className}
      style={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: align === "center" ? "center" : "flex-start",
        gap: "0.5rem",
        listStyle: "none",
        margin: 0,
        padding: 0,
      }}
    >
      {ids.map((id) => {
        const link = YUMESAKU_SOCIAL_LINKS[id];
        return (
          <li key={id}>
            <a
              className="ui-social-link"
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`ゆめさく公式の${link.label} (新しいタブで開く)`}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.4rem",
                minHeight: 44,
                padding: "0 0.5rem 0 0.25rem",
                borderRadius: 9999,
                color: labelColor,
                fontSize: "0.9375rem",
                fontWeight: 500,
                textDecoration: "none",
              }}
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 26,
                  height: 26,
                  borderRadius: 9999,
                  background: circleBackground,
                  color: glyphColor,
                }}
              >
                {link.glyph}
              </span>
              {link.label}
            </a>
          </li>
        );
      })}
    </ul>
  );
}
